import { useEffect, useMemo, useState } from 'react';
import { hostTransactions } from '../api/host';
import { useCurrency } from '../components/CurrencyContext';

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const getDirection = (entry) => {
  const debit = String(entry?.debitAccount || entry?.debit_account || '');
  const credit = String(entry?.creditAccount || entry?.credit_account || '');
  if (credit.includes('HOST_PAYABLE')) return 'credit';
  if (debit.includes('HOST_PAYABLE')) return 'debit';
  return '';
};

const HostTransactions = ({ accessToken }) => {
  const { formatCurrency } = useCurrency();
  const [filters, setFilters] = useState({ from: '', to: '' });
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, limit: 20 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const formatMoney = (currency, amount) => {
    const numeric = Number(amount);
    if (!Number.isFinite(numeric)) return 'N/A';
    const value = numeric / 100;
    return formatCurrency(value, currency || 'INR') || `${currency || 'INR'} ${value}`;
  };

  const load = async (page = pagination.page || 1) => {
    setLoading(true);
    setError('');
    try {
      const res = await hostTransactions(accessToken, { ...filters, page, limit: pagination.limit });
      setItems(res.items || []);
      setPagination({ page: res.page || page, limit: res.limit || pagination.limit, total: res.total || 0 });
    } catch (err) {
      setError(err.message || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!accessToken) return;
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  const applyFilters = () => {
    load(1);
  };

  const pageCount = useMemo(() => {
    return Math.max(1, Math.ceil((pagination.total || 0) / (pagination.limit || 20)));
  }, [pagination]);

  return (
    <div className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-slate-200">
      <div className="flex items-center justify-between border-b border-slate-200 pb-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-emerald-700">Ledger</p>
          <h1 className="text-xl font-bold text-slate-900">Statement</h1>
          <p className="text-sm text-slate-600">Ledger statement of movements for your payouts.</p>
        </div>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-3">
        <input
          type="date"
          value={filters.from}
          onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
        />
        <input
          type="date"
          value={filters.to}
          onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
        />
        <button
          type="button"
          onClick={applyFilters}
          className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700"
        >
          Apply filters
        </button>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Reference</th>
              <th className="px-4 py-3">Accounts</th>
              <th className="px-4 py-3">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr>
                <td colSpan={4} className="px-4 py-3 text-slate-500">
                  Loading...
                </td>
              </tr>
            )}
            {error && !loading && (
              <tr>
                <td colSpan={4} className="px-4 py-3 text-rose-600">
                  {error}
                </td>
              </tr>
            )}
            {!loading && !items.length && !error && (
              <tr>
                <td colSpan={4} className="px-4 py-3 text-slate-500">
                  No transactions found.
                </td>
              </tr>
            )}
            {items.map((entry) => {
              const direction = getDirection(entry);
              const currency = entry.currency || entry.currency_code;
              const amountLabel = formatMoney(currency, entry.amount);
              const sign = direction === 'debit' ? '-' : direction === 'credit' ? '+' : '';
              const amountTone =
                direction === 'debit' ? 'text-rose-600' : direction === 'credit' ? 'text-emerald-600' : 'text-slate-700';
              const createdAt = entry.createdAt || entry.created_at;
              const referenceType = entry.referenceType || entry.reference_type || 'N/A';
              const referenceId = entry.referenceId || entry.reference_id || 'N/A';
              const debitAccount = entry.debitAccount || entry.debit_account || 'N/A';
              const creditAccount = entry.creditAccount || entry.credit_account || 'N/A';
              return (
                <tr key={entry.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-xs text-slate-600">{formatDateTime(createdAt)}</td>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-900">{referenceType}</div>
                    <div className="text-xs text-slate-500">{referenceId}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    <div>{debitAccount}</div>
                    <div className="text-slate-400">to</div>
                    <div>{creditAccount}</div>
                  </td>
                  <td className={`px-4 py-3 font-semibold ${amountTone}`}>{`${sign}${amountLabel}`}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center justify-between text-sm text-slate-600">
        <div>
          Page {pagination.page || 1} of {pageCount}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => load(Math.max(1, (pagination.page || 1) - 1))}
            className="rounded-lg border border-slate-200 px-3 py-1 hover:bg-slate-50"
            disabled={(pagination.page || 1) <= 1}
          >
            Prev
          </button>
          <button
            type="button"
            onClick={() => load((pagination.page || 1) + 1)}
            className="rounded-lg border border-slate-200 px-3 py-1 hover:bg-slate-50"
            disabled={(pagination.page || 1) >= pageCount}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default HostTransactions;
